/**
 * Exactly one filter panel (ColumnHeaderFilter) may be open across the app. A local `open`
 * boolean cannot express that on its own — a sibling has no way to learn that another opened.
 * This single-slot registry holds the closer for whichever panel is currently open; claiming the
 * slot closes the previous holder.
 *
 * Split into its own module (2026-11-02) so `isFilterPanelOpen` can be imported by page-level code
 * (§16.5's Escape-then-clear-`q` handling) without pulling in ColumnHeaderFilter.tsx's own
 * component export -- a file that exports both a component and a plain function breaks Fast
 * Refresh (`react-refresh/only-export-components`), which this split avoids rather than suppresses.
 *
 * Deliberately NOT a useSyncExternalStore subscription: that re-renders every mounted instance on
 * every toggle (a 10-column table re-renders 10 filters per click). Measured on
 * ColumnHeaderFilter.test.tsx, the subscription form took the operator-switch test from 2.7s to
 * 7.8s. Here only the panel actually closing re-renders.
 */
let closeOpenPanel: (() => void) | null = null;

export function claimOpenPanel(close: () => void) {
  if (closeOpenPanel && closeOpenPanel !== close) closeOpenPanel();
  closeOpenPanel = close;
}

export function releaseOpenPanel(close: () => void) {
  if (closeOpenPanel === close) closeOpenPanel = null;
}

/** §16.5: a page-level Escape handler (for the search `q` param) needs to know whether a filter
 * panel is open BEFORE this same Escape press closes it, so the first Escape closes the panel and
 * only a second, later one (no panel open) clears `q`. Call this from a `document` keydown
 * listener registered with `{capture: true}` -- capture fires top-down before the panel's own
 * bubble-phase Escape handler runs on the panel itself, so it still sees the pre-dismissal state. */
export function isFilterPanelOpen(): boolean {
  return closeOpenPanel !== null;
}
